import React, { useEffect, useState } from "react";
import styled from "styled-components";
import FlavorChart from "./FlavorChart";
import { palette } from "@/styles/palette";
import TastingNote from "../common/TastingNote";
import {
    fetchProductReviewSummary,
    fetchProductFlavorTop5,
    ReviewSummaryData,
    FlavorData
} from "@/api/productEvaluationApi";
import dayjs from "dayjs";
import logger from "@/utils/logger";

interface TastingNoteType {
    label: string;
    value: number;
    description?: string;
}

interface ProductEvaluationProps {
    productAltKey: string;
    tastingNotes?: TastingNoteType[];
    flavorData?: FlavorData[];
}

const ProductEvaluation: React.FC<ProductEvaluationProps> = ({
    productAltKey,
    tastingNotes: initialTastingNotes,
    flavorData: initialFlavorData
}) => {
    const [reviewSummary, setReviewSummary] = useState<ReviewSummaryData | null>(null);
    const [flavorData, setFlavorData] = useState<FlavorData[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<boolean>(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                // 리뷰 요약 데이터 가져오기
                const summaryData = await fetchProductReviewSummary(productAltKey);
                setReviewSummary(summaryData);

                // 풍미 데이터 가져오기
                const flavors = await fetchProductFlavorTop5(productAltKey);
                setFlavorData(flavors);
            } catch (err) {
                logger.error("데이터 로딩 중 오류 발생:", err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [productAltKey]);

    // 리뷰 요약에서 테이스팅 노트 데이터 생성
    const tastingNotes = reviewSummary
        ? [
              { label: "신맛", value: reviewSummary.avgSourness },
              { label: "단맛", value: reviewSummary.avgSweetness },
              { label: "향의 강도", value: reviewSummary.avgAromaStrength }
          ]
        : initialTastingNotes || [];

    // 날짜 포맷팅 함수 수정 (이미 한국 시간으로 내려오므로 단순 포맷팅만)
    const formatUpdateDate = (dateString: string) => {
        return dayjs(dateString).format("YY년 M월 D일 HH시");
    };

    if (loading) {
        return <LoadingMessage>데이터를 불러오는 중입니다...</LoadingMessage>;
    }

    if (error) {
        return <ErrorMessage>데이터를 불러오는 중 오류가 발생했습니다.</ErrorMessage>;
    }

    // 리뷰가 없는 경우
    if (!reviewSummary || reviewSummary.reviewCount === 0) {
        return (
            <NoReviewMessage>
                <p>아직 리뷰가 없어요.</p>
                <p>첫 번째 시음자가 되어보세요!</p>
            </NoReviewMessage>
        );
    }

    // 풍미 데이터가 있는지 확인
    const hasFlavorData = flavorData && flavorData.length > 0;

    return (
        <EvaluationWrapper>
            <UpdateInfoBar>
                <InfoIcon>ⓘ</InfoIcon>
                <InfoText>{formatUpdateDate(reviewSummary.updatedAt)} 기준으로 업데이트</InfoText>
            </UpdateInfoBar>
            <Section>
                <SectionTitle>다른 시음자는 이렇게 느꼈어요</SectionTitle>
                <TastingNoteList>
                    {tastingNotes.map((note, index) => (
                        <TastingNote key={index} label={note.label} value={note.value} readOnly={true} />
                    ))}
                </TastingNoteList>
            </Section>

            <Section>
                <SectionTitle>다른 시음자가 느낀 풍미 TOP5</SectionTitle>
                {hasFlavorData ? (
                    <FlavorChart data={flavorData} />
                ) : (
                    <NoDataMessage>아직 풍미 평가가 없어요.</NoDataMessage>
                )}
            </Section>
        </EvaluationWrapper>
    );
};

const EvaluationWrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const Section = styled.section`
    width: 100%;
    margin-bottom: 28px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const SectionTitle = styled.h3`
    font-size: 18px;
    font-weight: 600;
    color: ${palette.gray[900]};
    margin-bottom: 20px;
`;

const TastingNoteList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
`;

const ReviewCount = styled.p`
    font-size: 14px;
    color: ${palette.gray[600]};
    margin-bottom: 16px;
`;

const LoadingMessage = styled.div`
    width: 100%;
    padding: 40px 0;
    text-align: center;
    color: ${palette.gray[600]};
    font-size: 16px;
`;

const ErrorMessage = styled.div`
    width: 100%;
    padding: 40px 0;
    text-align: center;
    color: ${palette.red[500]};
    font-size: 16px;
`;

const NoReviewMessage = styled.div`
    width: 100%;
    padding: 60px 20px;
    text-align: center;
    background-color: ${palette.gray[100]};
    border-radius: 8px;

    p {
        font-size: 16px;
        color: ${palette.gray[700]};
        margin-bottom: 8px;

        &:last-child {
            margin-bottom: 0;
            color: ${palette.gray[500]};
            font-size: 14px;
        }
    }
`;

const NoDataMessage = styled.div`
    width: 100%;
    padding: 30px 0;
    text-align: center;
    color: ${palette.gray[600]};
    font-size: 14px;
    background-color: ${palette.gray[100]};
    border-radius: 8px;
`;

const UpdateInfoBar = styled.div`
    display: flex;
    align-items: flex-start;
    width: 100%;
    background-color: ${palette.blue[50]};
    border-radius: 8px;
`;

const InfoIcon = styled.span`
    font-size: 14px;
    font-weight: bold;
    color: ${palette.blue[500]};
    margin-right: 6px;
    line-height: 1.4;
`;

const InfoText = styled.p`
    font-size: 12px;
    color: ${palette.gray[700]};
    line-height: 1.4;
    text-align: left;
`;

export default ProductEvaluation;
